import { Store, Percent } from 'lucide-react';

interface PricingSummaryCardProps {
  label: string;
  isMonthly: boolean;
  duration: number;
  totalTTC: number;
  monthlyClient: number | null;
  monthlySeller: number | null;
  feePercentage: number | null;
  feeAmountTTC: number | null;
  hideSellerRevenue?: boolean;
}

const fmt = (n: number) => n.toFixed(2).replace('.', ',');

export function PricingSummaryCard({
  label,
  isMonthly,
  duration,
  totalTTC,
  monthlyClient,
  monthlySeller,
  feePercentage,
  feeAmountTTC,
  hideSellerRevenue = false,
}: PricingSummaryCardProps) {
  const safeFeePercentage = Number(feePercentage) || 0;
  const safeFeeAmount = Number(feeAmountTTC) || 0;
  const hasFees = safeFeePercentage > 0 && safeFeeAmount > 0;
  const hasMonthlySellerBreakdown = isMonthly && monthlySeller !== null;

  return (
    <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-overflow-hidden tw-shadow-sm tw-flex tw-flex-col">
      {/* Header — prix dominant */}
      <div className="tw-bg-affinicar-primary/5 tw-px-5 tw-py-4 tw-border-b tw-border-affinicar-primary/20 tw-flex-1">
        <div className="tw-text-xs tw-font-semibold tw-text-affinicar-primary tw-uppercase tw-tracking-wider tw-mb-1">
          {label}
        </div>
        <div className="tw-flex tw-items-baseline tw-gap-2">
          <span className="tw-text-2xl tw-font-bold tw-text-gray-900">
            {isMonthly && monthlyClient !== null
              ? `${fmt(monthlyClient)} €`
              : `${fmt(totalTTC)} €`}
          </span>
          <span className="tw-text-sm tw-text-gray-500">{isMonthly ? '/mois TTC' : 'TTC'}</span>
        </div>
        {isMonthly && monthlyClient !== null && (
          <div className="tw-text-sm tw-text-gray-500 tw-mt-0.5">
            soit {duration} x {fmt(monthlyClient)} = {fmt(monthlyClient * duration)} € TTC
          </div>
        )}
      </div>

      {/* Breakdown */}
      {(hasMonthlySellerBreakdown || hasFees) && (
        <div className="tw-divide-y tw-divide-gray-100">
          {/* Frais de service */}
          {hasFees && (
            <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Percent className="tw-w-3.5 tw-h-3.5 tw-text-amber-600" />
                <span className="tw-text-sm tw-text-gray-600">Frais de service</span>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-text-xs tw-font-medium tw-bg-amber-50 tw-text-amber-700 tw-rounded-full tw-px-2.5 tw-py-0.5 tw-border tw-border-amber-200">
                  {safeFeePercentage.toFixed(2).replace('.', ',')}%
                </span>
                <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                  {fmt(safeFeeAmount)} €{isMonthly ? ' /mois' : ' TTC'}
                </span>
              </div>
            </div>
          )}

          {/* Montant vendeur — mensuel */}
          {hasMonthlySellerBreakdown && !hideSellerRevenue && (
            <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Store className="tw-w-3.5 tw-h-3.5 tw-text-emerald-600" />
                <span className="tw-text-sm tw-text-gray-600">Perçu par le vendeur</span>
              </div>
              <div className="tw-text-right">
                <span className="tw-text-sm tw-font-semibold tw-text-emerald-700">{fmt(monthlySeller!)} € /mois</span>
                <span className="tw-text-xs tw-text-gray-400 tw-ml-1.5">({fmt(monthlySeller! * duration)} € TTC)</span>
              </div>
            </div>
          )}

          {/* Montant vendeur — comptant */}
          {!isMonthly && hasFees && !hideSellerRevenue && (
            <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Store className="tw-w-3.5 tw-h-3.5 tw-text-emerald-600" />
                <span className="tw-text-sm tw-text-gray-600">Perçu par le vendeur</span>
              </div>
              <span className="tw-text-sm tw-font-semibold tw-text-emerald-700">{fmt(totalTTC - safeFeeAmount)} € TTC</span>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
